import { type Observable } from 'rxjs'
import { type JobChangeEvent } from '@/features/jobs/JobStore'
import { type Message } from '@/features/messaging/Message'
import type { FetchJobFn } from '@/features/jobs'
import { mergeMap } from 'rxjs/operators'
import { max } from 'date-fns'
import { filterNonNil } from '@/utils/rx-util'

/**
 * Synthesizes Job Update events from new message events.
 *
 * @param newMessages$
 * @param fetchJob
 */
export function synthesizedJobUpdates(
  newMessages$: Observable<Message>,
  fetchJob: FetchJobFn,
): Observable<JobChangeEvent> {
  return newMessages$.pipe(
    mergeMap(async (message): Promise<JobChangeEvent | null> => {
      if (message.conversation.meta.type !== 'Job') {
        return null
      }
      // A job that fails to load only misses this synthesized update.
      const job = await fetchJob(message.conversation.meta.id).catch(() => null)
      if (!job) {
        return null
      }

      // Always emit the `patch` event but use the latest modified.
      const newModified = max([message.dateCreated, job.dateModified])
      job.setModified(newModified)
      return {
        event: 'patched',
        data: job,
      }
    }),
    filterNonNil(),
  )
}
